/* Payment Mode Compliance — ops check that orders go to courier accounts that can carry their payment mode.
   (Sidebar: Courier Performance → Payment Mode Compliance. Backend API keeps its /api/ops/payment-mode-guard path.)

   One question per row: for every account restricted to a single payment mode (prepaid-only / COD-only),
   how many orders of the mode it CANNOT carry were booked in the window. Expected: 0 everywhere. Accounts
   with unset flags are listed separately (amber) with both counts so ops can decide the flag. Accounts that
   carry both modes cannot be wrong and are not listed.

   Backend: GET /api/ops/payment-mode-guard?from=yyyy-MM-dd&to=yyyy-MM-dd (ROLE_PROSHIPADMIN; inclusive IST
   booked-date window, max 31 days, to <= today; computed on every request — counted against the accounts'
   CURRENT flags, so correcting a flag reclassifies its orders immediately).
   Click a row → the account's deep-dive page. */
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import {Box, Button, Chip, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from '@mui/material'
import PageTitle from '../../../components/PageTitle'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import {SupplyHero, SupplyTable, KpiStrip, TableSkeleton, LoadErrorPanel, SideDrawer, exportTable} from '../components'
import {apiConstants, get} from '../../../common/constants'
import {apiError} from '../supplyMeta'


/* Drill-down table columns. `unknown` = account with a flag not set: the "why not blocked" column is dropped
   there because nothing was in place to block those orders. Shared by the table and its Export. */
const drillColumns = (unknown) => [
  {
    key: 'awbNumber',
    label: 'AWB / Order ID',
    value: (o) => `${o.awbNumber || ''} ${o.reference || ''}`,
    render: (o) => (
      <Box>
        <Typography variant='body2' sx={{fontFamily: MONO, fontWeight: 600}}>{o.awbNumber || '—'}</Typography>
        <Typography variant='caption' sx={{fontFamily: MONO, color: 'text.secondary'}}>{o.reference}</Typography>
      </Box>
    ),
  },
  {key: 'merchant', label: 'Merchant', value: (o) => o.merchant},
  {key: 'paymentMode', label: 'Payment mode', value: (o) => o.paymentMode, render: (o) => <Chip size='small' label={o.paymentMode} sx={{fontWeight: 600}} />},
  {
    key: 'codAmount',
    label: 'COD amount (₹)',
    align: 'right',
    value: (o) => o.codAmount,
    // a prepaid order has no COD amount; showing 0 there reads like a fact about the order
    render: (o) => (
      <Box component='span' sx={{fontFamily: MONO}}>
        {o.codAmount == null || !/cod/i.test(o.paymentMode || '') ? '—' : Number(o.codAmount).toLocaleString('en-IN')}
      </Box>
    ),
  },
  {key: 'orderStatus', label: 'Status', value: (o) => o.orderStatus},
  {
    key: 'bookedAt',
    label: 'Booked on (IST)',
    // local (IST) day, not toISOString(): a 01:30 IST booking would otherwise export as the previous UTC day
    value: (o) => (o.bookedAt ? `${isoDay(o.bookedAt)} ${new Date(o.bookedAt).toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit', hour12: false})}` : ''),
    render: (o) =>
      o.bookedAt ? (
        <Box>
          <Typography variant='body2' sx={{fontFamily: MONO}}>{fmtDay(isoDay(o.bookedAt))}</Typography>
          <Typography variant='caption' sx={{fontFamily: MONO, color: 'text.secondary'}}>
            {new Date(o.bookedAt).toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit', hour12: false})}
          </Typography>
        </Box>
      ) : (
        '—'
      ),
  },
  ...(unknown
    ? []
    : [
        {
          key: 'cause',
          label: 'Why it was not blocked',
          value: (o) => `${(CAUSE[o.cause] || {}).label || o.cause}: ${o.causeDetail || ''}`,
          render: (o) => {
            const c = CAUSE[o.cause] || {label: o.cause, tone: 'grey', help: ''}
            const color = c.tone === 'red' ? RED : c.tone === 'amber' ? AMBER : 'text.secondary'
            return (
              <Tooltip title={c.help}>
                <Box>
                  <Typography variant='body2' sx={{fontWeight: 600, color}}>{c.label}</Typography>
                  <Typography variant='caption' sx={{color: 'text.secondary'}}>{o.causeDetail}</Typography>
                </Box>
              </Tooltip>
            )
          },
        },
      ]),
]
const drillExportName = (row) => `payment-mode-compliance-${(row.cpAccountCode || 'account').replace(/[^A-Za-z0-9_-]+/g, '_')}`

/* Cause labels for the AWB drill-down — plain English for ops, keyed by the backend enum. */
const CAUSE = {
  CONVERTED_AFTER_BOOKING: {label: 'COD/prepaid changed after booking', tone: 'red', help: 'The order’s payment mode was changed from the panel after the AWB was already generated.'},
  AGGREGATOR_REBOOKED: {label: 'Clickpost booked on a different account', tone: 'amber', help: 'We allocated one courier account; Clickpost booked the shipment on another.'},
  MANUAL_SELECTION_FLAG_ALLOWED: {label: 'Picked manually; flag allowed it then', tone: 'grey', help: 'Courier was chosen by hand. The flag allowed this mode at the time and was changed later.'},
  FLAG_ALLOWED_AT_BOOKING: {label: 'Flag allowed it at booking', tone: 'grey', help: 'Nothing stopped it at booking: the flag was blank or allowed this mode then (or the order predates the check) and was changed later.'},
}

const RED = '#B42318'
const AMBER = '#B54708'
const GREEN = '#067647'
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace"

/* What the two flags add up to, in ops language: which payment mode this account is allowed to ship. */
const CAN_CARRY_LABEL = {
  PREPAID_ONLY: 'Prepaid orders only',
  COD_ONLY: 'COD orders only',
  NEITHER: 'No orders (both flags No)',
  UNKNOWN: 'Undecided (a flag is not set)',
}
/* Short form for the table chip — always an answer to the column header "Allowed to ship":
     restricted            → "Prepaid only" / "COD only" / "Nothing"
     Prepaid Yes, COD blank → "Prepaid · COD not decided"
     Prepaid No,  COD blank → "No Prepaid · COD not decided"
     both blank             → "Not decided yet" */
const CAN_CARRY_CHIP = {PREPAID_ONLY: 'Prepaid only', COD_ONLY: 'COD only', NEITHER: 'Nothing'}
const flagText = (v) => (v === true ? 'Yes' : v === false ? 'No' : 'not set')
/* the supply theme title-cases chip labels ("COD Not Decided"); these are sentences, keep them as written */
const NO_CAPS = {textTransform: 'none', '& .MuiChip-label': {textTransform: 'none'}}

/* Per-mode status pill for an undecided account: "Prepaid ✓ allowed" (green), "COD ✕ not allowed" (red), "COD ? not set" (amber). */
function ModePill({mode, flag}) {
  const st = flag === true ? {mark: '✓', txt: 'allowed', color: GREEN, bg: 'rgba(6,118,71,.10)'}
    : flag === false ? {mark: '✕', txt: 'not allowed', color: RED, bg: 'rgba(180,35,24,.10)'}
    : {mark: '?', txt: 'not set', color: AMBER, bg: 'rgba(181,71,8,.10)'}
  return (
    <Box component='span' sx={{display: 'inline-flex', alignItems: 'baseline', gap: 0.5, px: 1, py: 0.25, borderRadius: '6px', bgcolor: st.bg, color: st.color, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap'}}>
      <Box component='span' sx={{color: 'text.primary'}}>{mode}</Box>
      <Box component='span'>{st.mark}</Box>
      <Box component='span'>{st.txt}</Box>
    </Box>
  )
}
const allowedToShip = (r) => {
  if (r.canCarry !== 'UNKNOWN') return CAN_CARRY_CHIP[r.canCarry] || r.canCarry
  if (r.isCod == null && r.isPrepaid == null) return 'Not decided yet'
  const decided = r.isCod == null ? (r.isPrepaid ? 'Prepaid' : 'No Prepaid') : r.isCod ? 'COD' : 'No COD'
  const open = r.isCod == null ? 'COD' : 'Prepaid'
  return `${decided} · ${open} not decided`
}

/* A count cell: grey 0, red when it is a wrong-mode count, amber when the flag is unknown. */
/* A count chip. `label` (e.g. "COD") is printed inside the chip so a pair of counts reads
   "COD 34 · Prepaid 37" without hovering. */
function Count({n, tone, label}) {
  const color = n === 0 ? 'text.disabled' : tone === 'amber' ? AMBER : RED
  const bg = n === 0 ? 'transparent' : tone === 'amber' ? 'rgba(181,71,8,.10)' : 'rgba(180,35,24,.10)'
  return (
    <Box
      component='span'
      sx={{display: 'inline-flex', alignItems: 'baseline', gap: 0.5, minWidth: 44, justifyContent: 'flex-end', px: 1.25, py: 0.25, borderRadius: '6px', fontFamily: MONO, fontWeight: n === 0 ? 400 : 700, color, bgcolor: bg}}
    >
      {label ? (
        <Box component='span' sx={{fontFamily: 'inherit', fontSize: 11, fontWeight: 500, letterSpacing: '.04em', textTransform: 'uppercase', color: n === 0 ? 'text.disabled' : 'text.secondary'}}>
          {label}
        </Box>
      ) : null}
      {n}
    </Box>
  )
}

/* "COD,PREPAID" → "COD · Prepaid" for display. */
const modeText = (m) =>
  String(m || '')
    .split(',')
    .filter(Boolean)
    .map((x) => (x === 'PREPAID' ? 'Prepaid' : x))
    .join(' · ')

/* ── date range helpers (IST calendar days, yyyy-MM-dd) ─────────────────────────────── */
const MAX_RANGE_DAYS = 31 // "30 days or a month"; the backend enforces the same limit
const isoDay = (d) => {
  const x = new Date(d)
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
}
const addDays = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number)
  return isoDay(new Date(y, m - 1, d + n))
}
const spanDays = (from, to) => {
  const [y1, m1, d1] = from.split('-').map(Number)
  const [y2, m2, d2] = to.split('-').map(Number)
  return Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000) + 1
}
const fmtDay = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'})
const today = () => isoDay(new Date())
const lastNDays = (n) => ({from: addDays(today(), -(n - 1)), to: today()})
const thisMonth = () => ({from: today().slice(0, 8) + '01', to: today()})
/* Quick picks; the toggle highlights whichever one matches the range currently shown (none when hand-picked). */
const PRESETS = {'7d': () => lastNDays(7), '30d': () => lastNDays(30), month: thisMonth}
const presetFor = (range) => Object.keys(PRESETS).find((k) => {
  const p = PRESETS[k]()
  return p.from === range.from && p.to === range.to
}) || null
/* one height for every control in the filter bar (MUI small TextField is 40px) */
const CONTROL_H = 40
/* returns an error string, or null when the range is usable */
const rangeError = ({from, to}) => {
  if (!from || !to) return 'Pick both dates.'
  if (to > today()) return 'End date cannot be in the future.'
  if (from > to) return 'Start date must be on or before the end date.'
  const n = spanDays(from, to)
  if (n > MAX_RANGE_DAYS) return `Range is ${n} days; the maximum is ${MAX_RANGE_DAYS} (about a month).`
  return null
}

export default function PaymentModeCompliance() {
  /* `draft` follows the date inputs; `range` is what the page is actually showing (set on Apply / preset) */
  const [draft, setDraft] = useState(() => lastNDays(7))
  const [range, setRange] = useState(() => lastNDays(7))
  const draftError = rangeError(draft)
  const rangeLabel = `${fmtDay(range.from)} – ${fmtDay(range.to)}`
  const days = spanDays(range.from, range.to)
  const activePreset = presetFor(range)
  const applyRange = (r) => {
    setDraft(r)
    if (!rangeError(r)) setRange(r)
  }
  /* Which rows the table shows: 'attention' (default) = breaches + unset-flag accounts that carried orders of the
     unset mode; 'unknown' = every unset-flag account; 'all' = every listed account. */
  const [view, setView] = useState('attention')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  /* AWB drill-down: {row, loading, error, data} — opened by clicking a table row */
  const [drill, setDrill] = useState(null)

  const openDrill = useCallback(
    (row) => {
      if (!(row.wrongModeOrders > 0 || row.unverifiedOrders > 0)) return // nothing to drill into on a 0 row
      setDrill({row, loading: true, error: null, data: null})
      get(apiConstants.PAYMENT_MODE_GUARD_ORDERS, {courierId: row.courierId, from: range.from, to: range.to})
        .then((res) => setDrill({row, loading: false, error: null, data: res.data}))
        .catch((e) => setDrill({row, loading: false, error: apiError(e), data: null}))
    },
    [range.from, range.to]
  )

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    get(apiConstants.PAYMENT_MODE_GUARD, {from: range.from, to: range.to})
      .then((res) => setData(res.data))
      .catch((e) => setError(apiError(e)))
      .finally(() => setLoading(false))
  }, [range.from, range.to])

  useEffect(() => {
    load()
  }, [load])

  const allRows = useMemo(() => (data && data.rows) || [], [data])
  const attentionRows = useMemo(() => allRows.filter((r) => r.wrongModeOrders > 0 || r.unverifiedOrders > 0), [allRows])
  const unknownRows = useMemo(() => allRows.filter((r) => r.canCarry === 'UNKNOWN'), [allRows])
  const rows = view === 'all' ? allRows : view === 'unknown' ? unknownRows : attentionRows

  const problems = data ? data.accountsWithWrongModeOrders : 0
  const wrongTotal = data ? data.wrongModeOrdersTotal : 0
  const restricted = data ? data.restrictedAccounts : 0
  const unknown = data ? data.unknownFlagAccounts : 0
  const unknownWithOrders = unknownRows.filter((r) => r.unverifiedOrders > 0).length
  const allClear = data && problems === 0

  /* Card 1 counts ACCOUNTS, card 2 counts ORDERS on those accounts; card 3 is a flag-hygiene count that is
     deliberately NOT part of 1 or 2 (an unset flag makes an order unverifiable, not wrong). */
  const kpis = [
    {
      label: 'Restricted accounts in breach',
      value: problems,
      sub: `of ${restricted} prepaid-only / COD-only accounts`,
      accent: allClear ? GREEN : RED,
      onClick: () => setView('attention'),
    },
    {
      label: 'Wrong-mode orders',
      value: wrongTotal,
      sub: problems ? `on those ${problems} account${problems === 1 ? '' : 's'}` : 'on restricted accounts in this window',
      accent: allClear ? GREEN : RED,
      onClick: () => setView('attention'),
    },
    {
      label: 'Accounts with a flag not set',
      value: unknown,
      sub: unknownWithOrders ? `${unknownWithOrders} of them carried orders we can’t judge yet` : 'none carried orders in this window',
      accent: unknown ? AMBER : GREEN,
      onClick: () => setView('unknown'),
    },
  ]

  const columns = [
    {
      key: 'account',
      label: 'Courier account',
      value: (r) => `${r.cpAccountCode || ''} ${r.name || ''} ${r.parent || ''}`,
      render: (r) => (
        <Box>
          <Typography variant='body2' sx={{fontWeight: 600}}>{r.cpAccountCode || r.name}</Typography>
          {/* parent courier only (Prozo ID is in the export); no shipment type — the page is B2C only */}
          <Typography variant='caption' sx={{color: 'text.secondary'}}>{r.parent}</Typography>
        </Box>
      ),
    },
    /* Two fact columns only. "Allowed to ship" is the two flags summed up (the raw flags are in the export);
       "Orders found" names the mode it counted, so no separate Flags / Checked-for columns. */
    {
      key: 'canCarry',
      label: 'Allowed to ship',
      // export carries the raw flags too, so the sheet can be fed back into the bulk attribute upload
      value: (r) => `${allowedToShip(r)} (COD: ${flagText(r.isCod)}, Prepaid: ${flagText(r.isPrepaid)})`,
      /* Restricted account: one chip ("Prepaid only"). Undecided account: one small pill PER MODE so each mode
         has its own plain status — [Prepaid ✓ allowed] [COD ? not set] — instead of two facts in one chip. */
      render: (r) =>
        r.canCarry === 'UNKNOWN' ? (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start'}}>
            <ModePill mode='Prepaid' flag={r.isPrepaid} />
            <ModePill mode='COD' flag={r.isCod} />
          </Box>
        ) : (
          <Chip size='small' label={allowedToShip(r)} sx={{fontWeight: 600, bgcolor: 'action.hover', color: 'text.primary', ...NO_CAPS}} />
        ),
    },
    {
      key: 'wrongModeOrders',
      label: 'Orders found',
      align: 'right',
      // numeric so the column sorts as a number; the export's "Allowed to ship" cell already says whether it is undecided
      value: (r) => r.wrongModeOrders + (r.unverifiedOrders || 0),
      /* No caption: the "Allowed to ship" chip already says why the count matters (red = breach, amber = undecided). */
      render: (r) =>
        r.canCarry === 'UNKNOWN' ? (
          <Box component='span'>
            {String(r.unverifiedMode || '').includes('COD') ? <Count n={r.codOrders} tone='amber' label='COD' /> : null}{' '}
            {String(r.unverifiedMode || '').includes('PREPAID') ? <Count n={r.prepaidOrders} tone='amber' label='Prepaid' /> : null}
          </Box>
        ) : r.canCarry === 'NEITHER' ? (
          <Box component='span'>
            <Count n={r.codOrders} tone='red' label='COD' /> <Count n={r.prepaidOrders} tone='red' label='Prepaid' />
          </Box>
        ) : (
          <Count n={r.wrongModeOrders} tone='red' label={modeText(r.wrongMode)} />
        ),
    },
  ]

  return (
    <>
      <PageTitle>{'Payment Mode Compliance'}</PageTitle>
      <SupplyHero
        icon={<VerifiedUserOutlinedIcon />}
        title='Payment Mode Compliance'
        subtitle='COD and prepaid orders booked on B2C courier accounts that are restricted to one payment mode.'
      />

      {/* Filter bar — its own row under the banner. Every control is CONTROL_H tall so line one reads as one line:
          Booked between [From] to [To] [Apply] | [Last 7 days | Last 30 days | This month]
          Showing … · max 31 days                                                        ↻ Refresh
          Booked-date window, inclusive IST days, max 31 days (backend enforces the same). */}
      <Box sx={{mb: 2, p: 1.5, borderRadius: '10px', border: 1, borderColor: 'divider', bgcolor: 'background.paper'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
          <Typography variant='body2' sx={{fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap'}}>Booked between</Typography>
          <TextField
            id='pmc-from'
            type='date'
            size='small'
            value={draft.from}
            onChange={(e) => setDraft((d) => ({...d, from: e.target.value}))}
            inputProps={{max: today(), 'aria-label': 'From date'}}
            sx={{width: 170, '& .MuiInputBase-root': {height: CONTROL_H}}}
          />
          <Typography variant='body2' sx={{color: 'text.secondary'}}>to</Typography>
          <TextField
            id='pmc-to'
            type='date'
            size='small'
            value={draft.to}
            onChange={(e) => setDraft((d) => ({...d, to: e.target.value}))}
            inputProps={{max: today(), 'aria-label': 'To date'}}
            sx={{width: 170, '& .MuiInputBase-root': {height: CONTROL_H}}}
          />
          <Button variant='contained' disableElevation onClick={() => applyRange(draft)} disabled={!!draftError || loading} sx={{height: CONTROL_H, textTransform: 'none', fontWeight: 600, px: 2.5}}>
            Apply
          </Button>

          {/* '1px', not 1 — in sx a bare 1 means 100% width and turns this hairline into a full-width bar */}
          <Box sx={{width: '1px', height: CONTROL_H - 12, bgcolor: 'divider', mx: 0.5, display: {xs: 'none', md: 'block'}}} />

          <ToggleButtonGroup
            size='small'
            exclusive
            value={activePreset}
            onChange={(_, v) => v && applyRange(PRESETS[v]())}
            sx={{height: CONTROL_H, '& .MuiToggleButton-root': {px: 1.75, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap'}}}
          >
            <ToggleButton value='7d'>Last 7 days</ToggleButton>
            <ToggleButton value='30d'>Last 30 days</ToggleButton>
            <ToggleButton value='month'>This month</ToggleButton>
          </ToggleButtonGroup>

        </Box>
        {/* second line by design: what is on screen (left) and the re-run action (right) — never depends on wrapping */}
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 1}}>
          <Typography variant='caption' sx={{color: draftError ? RED : 'text.secondary', fontWeight: draftError ? 600 : 400}}>
            {draftError ||
              `Showing ${rangeLabel} (${days} day${days === 1 ? '' : 's'}) · max ${MAX_RANGE_DAYS} days` +
                (data && data.generatedAt ? ` · live, based on current flags · refreshed ${new Date(data.generatedAt).toLocaleTimeString()}` : '')}
          </Typography>
          <Button size='small' variant='text' startIcon={<RefreshOutlinedIcon fontSize='small' />} onClick={load} disabled={loading} sx={{textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0}}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {loading && !data ? (
        <TableSkeleton rows={6} kpis={3} />
      ) : error ? (
        <LoadErrorPanel message={error} onRetry={load} title='Couldn’t load the payment mode check' />
      ) : (
        <Box sx={{opacity: loading ? 0.55 : 1, transition: 'opacity .2s', pointerEvents: loading ? 'none' : 'auto'}}>
          {/* No verdict banner: the three cards already carry the verdict (colour + number); repeating it in prose duplicated them. */}
          <KpiStrip cards={kpis} />

          {/* Pinned column widths (page-local CSS): otherwise the browser resizes columns to whatever text is on the
              current tab, and the layout jumps between "Needs attention" / "Flag not set" / "All". Rows at 0 have
              nothing to open, so they keep the pointer but openDrill ignores them. */}
          <Box
            sx={{
              '& table.tbl': {tableLayout: 'fixed'},
              '& table.tbl th:nth-of-type(1)': {width: '40%'},
              '& table.tbl th:nth-of-type(2)': {width: '35%'},
              '& table.tbl th:nth-of-type(3)': {width: '25%'},
            }}
          >
          <SupplyTable
            columns={columns}
            rows={() => rows}
            rowKey={(r) => r.courierId}
            searchPlaceholder='Search account or parent'
            searchKeys={['cpAccountCode', 'name', 'parent', 'prozoId']}
            defaultSort={{key: 'wrongModeOrders', dir: 'desc'}}
            rowClick={openDrill}
            exportName={`payment-mode-compliance-${range.from}_${range.to}`}
            emptyText={
              view === 'attention'
                ? 'Nothing needs attention: no wrong-mode orders, and no orders waiting on a flag decision.'
                : view === 'unknown'
                ? 'Every account has both flags set.'
                : 'No prepaid-only or COD-only accounts.'
            }
            toolbarRight={
              <ToggleButtonGroup size='small' exclusive value={view} onChange={(_, v) => v && setView(v)} sx={{'& .MuiToggleButton-root': {px: 1.5, textTransform: 'none', fontWeight: 600}}}>
                <ToggleButton value='attention'>Needs attention ({attentionRows.length})</ToggleButton>
                <ToggleButton value='unknown'>Flag not set ({unknownRows.length})</ToggleButton>
                <ToggleButton value='all'>All ({allRows.length})</ToggleButton>
              </ToggleButtonGroup>
            }
          />
          </Box>

        </Box>
      )}

      <SideDrawer
        open={!!drill}
        onClose={() => setDrill(null)}
        width={980}
        title={
          drill
            ? `${drill.row.cpAccountCode || drill.row.name} · ${rangeLabel}`
            : ''
        }
        footer={
          drill && drill.data && drill.data.truncated ? (
            <Typography variant='caption' sx={{color: 'text.secondary'}}>Showing the newest 500. Narrow the date range for the rest.</Typography>
          ) : null
        }
      >
        {drill && drill.loading ? (
          <TableSkeleton rows={5} />
        ) : drill && drill.error ? (
          <LoadErrorPanel message={drill.error} onRetry={() => openDrill(drill.row)} title='Couldn’t load the orders' />
        ) : drill && drill.data ? (
          <Box sx={{display: 'grid', gap: 1.5}}>
            {/* one line: the explanation on the left, Export on the right (the table's own toolbar is hidden below) */}
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
              {drill.data.canCarry === 'UNKNOWN' ? (
                <Typography variant='body2' sx={{color: 'text.secondary'}}>
                  {String(drill.data.unverifiedMode || '').includes(',') ? (
                    <>Neither the <b>COD</b> nor the <b>Prepaid</b> flag is set on this account, so these orders cannot be judged yet.</>
                  ) : (
                    <>The <b>{modeText(drill.data.unverifiedMode)}</b> flag is not set on this account, so these <b>{modeText(drill.data.unverifiedMode)}</b> orders cannot be judged yet.</>
                  )}
                </Typography>
              ) : (
                <Typography variant='body2' sx={{color: 'text.secondary'}}>
                  This account is allowed to ship <b>{CAN_CARRY_LABEL[drill.data.canCarry] || drill.data.canCarry}</b>, but these orders are <b>{modeText(drill.data.wrongMode)}</b>.
                </Typography>
              )}
              <Button
                size='small'
                variant='outlined'
                startIcon={<DownloadOutlinedIcon fontSize='small' />}
                disabled={!(drill.data.orders || []).length}
                onClick={() => exportTable(drill.data.orders || [], drillColumns(drill.data.canCarry === 'UNKNOWN'), drillExportName(drill.row))}
                sx={{textTransform: 'none', fontWeight: 600, flexShrink: 0}}
              >
                Export
              </Button>
            </Box>
            {/* the table's toolbar would hold only a second Export button here, so hide it (page-local override) */}
            <Box sx={{'& .card-pad': {display: 'none'}}}>
              <SupplyTable
                columns={drillColumns(drill.data.canCarry === 'UNKNOWN')}
                rows={() => drill.data.orders || []}
                rowKey={(o) => o.awbNumber || o.reference}
                hideSearch
                pageSize={25}
                exportName={false}
                emptyText={
                  drill.row.canCarry === 'UNKNOWN'
                    ? `No ${modeText(drill.row.unverifiedMode)} orders on this account in the window.`
                    : 'No wrong-mode orders on this account in the window.'
                }
              />
            </Box>
          </Box>
        ) : null}
      </SideDrawer>
    </>
  )
}
